/**
 * A meeting's *Repeats* — the presets, then **Custom…**, which opens the
 * options Outlook has (Aaron, Oct 23, note 4: "Use the ones outlook has -
 * that offers unlimited flexibility"; migration 0138).
 *
 * Every N days, weeks (on the weekdays ticked), months or years; a month or
 * a year on the same day number or on the first date's *nth weekday* (the
 * fourth Thursday, the last Friday); starting on the meeting's own day; and
 * ending never, on a date, or after N times. The words under it are
 * `recurrenceWords` — the same sentence the calendar's card says — and every
 * day it lands on is `seriesDates` on the server.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  REPEAT_MAX_COUNT, REPEAT_MAX_INTERVAL, isLastOfMonth, nthOfMonth, presetKeyOf, recurrenceWords, repeatPresets,
  weekdayIndex, type Recurrence, type RepeatFreq, type RepeatNth,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Field, FOCUS, MenuField, Segments, boxClass } from './kit.js'
import { DayField } from './when.js'

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December']
const NTH = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' } as Record<number, string>
const UNITS: Record<RepeatFreq, [string, string]> = {
  daily: ['Day', 'Days'], weekly: ['Week', 'Weeks'], monthly: ['Month', 'Months'], yearly: ['Year', 'Years'],
}

/** The menu: *Never · Every day · Every week on <weekday> · Every other week · Every month · Every year · Custom…*. */
export function RepeatsMenu({ date, value, onChange, onCustom, phone }: {
  date: string
  value: Recurrence | null
  onChange: (next: Recurrence | null) => void
  /** *Custom…* — open the panel. */
  onCustom: () => void
  phone: boolean
}) {
  const presets = repeatPresets(date)
  const key = presetKeyOf(value) ?? 'custom'
  return (
    <MenuField label="Repeats" phone={phone} value={key}
      options={[...presets.map((preset) => ({ value: preset.key, label: preset.label })),
        { value: 'custom', label: 'Custom…' }]}
      display={key === 'custom' && value
        ? <span className="truncate">{recurrenceWords(date, value)}</span> : undefined}
      onChange={(next) => {
        if (next === 'custom') { onCustom(); return }
        onChange(presets.find((preset) => preset.key === next)?.recurrence ?? null)
      }} />
  )
}

/** A whole number from a box, held between `min` and `max`; the box may be blank while it is typed in. */
function NumberBox({ label, value, onChange, min, max, phone }: {
  label: string; value: number; onChange: (next: number) => void; min: number; max: number; phone: boolean
}) {
  const [text, setText] = useState(String(value))
  useEffect(() => { setText(String(value)) }, [value])
  return (
    <span className={boxClass(phone, cn(FOCUS, 'shrink-0 justify-center'))} style={{ width: phone ? 64 : 52 }}>
      <input aria-label={label} inputMode="numeric" value={text}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, String(max).length)
          setText(digits)
          if (digits) onChange(Math.max(min, Math.min(max, Number(digits))))
        }}
        onBlur={() => setText(String(value))}
        className="w-full min-w-0 bg-transparent text-center font-semibold tabular-nums outline-none focus-visible:shadow-none" />
    </span>
  )
}

/** One choice of a radio group drawn as a row: the dot, then the words (and a control, for *On* and *After*). */
function Choice({ checked, onChoose, label, children, phone }: {
  checked: boolean; onChoose: () => void; label: string; children?: ReactNode; phone: boolean
}) {
  return (
    <span className={cn('flex items-center gap-2', phone ? 'min-h-[46px]' : 'min-h-9')}>
      <button type="button" role="radio" aria-checked={checked} aria-label={label} onClick={onChoose}
        className={cn('flex shrink-0 items-center gap-2 text-left', phone ? 'text-[15px]' : 'text-[13px]')}>
        <span aria-hidden className={cn('inline-flex h-4 w-4 items-center justify-center rounded-full border-[1.5px]',
          checked ? 'border-accent' : 'border-line-strong')}>
          {checked && <i className="block h-2 w-2 rounded-full bg-accent" />}
        </span>
        <span className={checked ? 'font-semibold text-text' : 'text-text'}>{label}</span>
      </button>
      {children}
    </span>
  )
}

/** The days a weekly series may fall on, normalised: the first date's own weekday alone is the default. */
function weekdaysFor(date: string, days: number[]): number[] | null {
  const sorted = [...new Set(days)].sort((a, b) => a - b)
  return sorted.length === 1 && sorted[0] === weekdayIndex(date) ? null : sorted
}

/** The panel *Custom…* opens, under *When* and *Repeats*. */
export function CustomRepeat({ date, value, onChange, onDone, phone, today }: {
  date: string
  value: Recurrence | null
  onChange: (next: Recurrence) => void
  onDone: () => void
  phone: boolean
  today: string
}) {
  const r: Recurrence = value ?? { freq: 'weekly', interval: 1, weekdays: null, monthNth: null, until: null, count: null }
  /* The panel always holds a repeat: opening it on *Never* starts from every week. */
  useEffect(() => { if (!value) onChange(r) }, [])
  const set = (part: Partial<Recurrence>) => onChange({ ...r, ...part })
  const weekday = weekdayIndex(date)
  const days = r.weekdays ?? [weekday]
  const nth = nthOfMonth(date)
  const last = isLastOfMonth(date)
  const [, m, d] = date.split('-').map(Number) as [number, number, number]
  const ends: 'never' | 'on' | 'after' = r.until ? 'on' : r.count !== null ? 'after' : 'never'
  const unitLabel = (freq: RepeatFreq) => UNITS[freq][r.interval === 1 ? 0 : 1]

  /* The nth-weekday choices this first date allows: *the fourth Thursday* (1–4), *the last Thursday* (its last). */
  const nths: RepeatNth[] = [...(nth <= 4 ? [nth as RepeatNth] : []), ...(last ? [-1 as RepeatNth] : [])]
  const dayWords = r.freq === 'yearly' ? `On ${MONTH_NAMES[m - 1]} ${d}` : `On day ${d}`
  const nthWords = (n: RepeatNth) => `On the ${NTH[n]} ${WEEKDAY_NAMES[weekday]}${r.freq === 'yearly' ? ` of ${MONTH_NAMES[m - 1]}` : ''}`

  return (
    <div role="group" aria-label="Custom repeat"
      className={cn('flex flex-col rounded-[12px] border border-line bg-surface-alt', phone ? 'gap-3 p-3.5' : 'gap-2.5 px-3.5 py-3')}>
      <Field label="Repeat every" phone={phone}>
        <span className="flex flex-wrap items-center gap-2">
          <NumberBox label="Repeat every" value={r.interval} min={1} max={REPEAT_MAX_INTERVAL} phone={phone}
            onChange={(interval) => set({ interval })} />
          <Segments label="Repeat unit" height={phone ? 36 : 28} value={r.freq}
            options={(['daily', 'weekly', 'monthly', 'yearly'] as const).map((freq) => ({ value: freq, label: unitLabel(freq) }))}
            onChange={(freq) => set({ freq, weekdays: null, monthNth: null })} />
        </span>
      </Field>

      {r.freq === 'weekly' && (
        <Field label="On" phone={phone}>
          <span role="group" aria-label="Weekdays" className="flex gap-1.5">
            {WEEKDAY_NAMES.map((name, index) => {
              const on = days.includes(index)
              return (
                <button key={name} type="button" role="checkbox" aria-checked={on} aria-label={name} title={name}
                  onClick={() => {
                    const next = on ? days.filter((one) => one !== index) : [...days, index]
                    /* A week with no day in it repeats nothing: the last tick stays. */
                    if (next.length > 0) set({ weekdays: weekdaysFor(date, next) })
                  }}
                  className={cn('inline-flex items-center justify-center rounded-full border font-semibold',
                    phone ? 'h-10 w-10 text-[14px]' : 'h-8 w-8 text-[12.5px]',
                    on ? 'border-accent bg-accent text-white' : 'border-line bg-surface text-text hover:bg-hover')}>
                  {name.charAt(0)}
                </button>
              )
            })}
          </span>
        </Field>
      )}

      {(r.freq === 'monthly' || r.freq === 'yearly') && (
        <div role="radiogroup" aria-label="On" className={cn('flex', phone ? 'flex-col' : 'flex-wrap gap-x-5')}>
          <Choice phone={phone} checked={r.monthNth === null} onChoose={() => set({ monthNth: null })} label={dayWords} />
          {nths.map((n) => (
            <Choice key={n} phone={phone} checked={r.monthNth === n} onChoose={() => set({ monthNth: n })} label={nthWords(n)} />
          ))}
        </div>
      )}

      <Field label="Ends" phone={phone}>
        <div role="radiogroup" aria-label="Ends" className={cn('flex', phone ? 'flex-col' : 'flex-wrap items-center gap-x-5')}>
          <Choice phone={phone} checked={ends === 'never'} onChoose={() => set({ until: null, count: null })} label="Never" />
          <Choice phone={phone} checked={ends === 'on'} label="On"
            onChoose={() => { if (ends !== 'on') set({ until: r.until ?? addMonths(date, 3), count: null }) }}>
            {ends === 'on' && (
              <span style={{ width: phone ? 170 : 150 }} className="flex">
                <DayField label="Ends on" value={r.until} today={today} phone={phone}
                  onChange={(until) => set({ until: until < date ? date : until, count: null })} />
              </span>
            )}
          </Choice>
          <Choice phone={phone} checked={ends === 'after'} label="After"
            onChoose={() => { if (ends !== 'after') set({ count: r.count ?? 10, until: null }) }}>
            {ends === 'after' && (
              <span className="flex items-center gap-1.5">
                <NumberBox label="Times" value={r.count ?? 10} min={1} max={REPEAT_MAX_COUNT} phone={phone}
                  onChange={(count) => set({ count, until: null })} />
                <span className={phone ? 'text-[15px]' : 'text-[13px]'}>{r.count === 1 ? 'time' : 'times'}</span>
              </span>
            )}
          </Choice>
        </div>
      </Field>

      <div className="flex items-center gap-2 border-t border-line-faint pt-2.5">
        <span className={cn('min-w-0 flex-1 text-text-muted', phone ? 'text-[13px]' : 'text-[12px]')}>
          {recurrenceWords(date, r)} · starts {shortDay(date)}
        </span>
        <button type="button" onClick={onDone}
          className={cn('shrink-0 rounded-lg border border-line bg-surface px-3 font-semibold hover:bg-hover',
            phone ? 'h-10 text-[15px]' : 'h-[30px] text-[12.5px]')}>
          Done
        </button>
      </div>
    </div>
  )
}

/** *Tue, Oct 27*. */
function shortDay(date: string): string {
  const [, m, d] = date.split('-').map(Number) as [number, number, number]
  return `${WEEKDAY_NAMES[weekdayIndex(date)]!.slice(0, 3)}, ${MONTH_NAMES[m - 1]!.slice(0, 3)} ${d}`
}

/** Three months on, for *Ends on* when it is first ticked (Outlook's own default). */
function addMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const index = y * 12 + (m - 1) + months
  const year = Math.floor(index / 12)
  const month = (index % 12) + 1
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, days)).padStart(2, '0')}`
}
